import { createReducer } from "@reduxjs/toolkit";

import {
  getConversations,
  getConversationsFailure,
  getConversationsSuccess,
  getMoreConversations,
  getMoreConversationsFailure,
  getMoreConversationsSuccess,
  getMessages,
  getMessagesSuccess,
  getMessagesFailure,
  getMoreMessages,
  getMoreMessagesSuccess,
  getMoreMessagesFailure,
  hideConversation,
  conversationUpdateSuccess,
  messageReceivedSuccess,
  messageReceivedUnreadCountSuccess,
  addMessage,
  removeMessage,
  deleteMessageSuccess,
  sendMessage,
  sendMessageSuccess,
  sendMessageFailure,
  setIsSentStatus,
  createConversation,
  createConversationSuccess,
  createConversationFailure,
  createConversationReset,
  getConversationDetails,
  getConversationDetailsSuccess,
  getConversationDetailsFailure,
  markAsReadSuccess,
  updateConversation,
  markMessagesInConversationRead,
  removeMessageFromConversation,
  leaveChat,
  getUnreadCountSuccess,
  editMessage,
  editMessageSuccess,
  editMessageFailure,
  setEditMessageId,
  resetEditMessageId,
  messageEditedSuccess,
  newConversationReceivedSuccess,
  addConversation,
  addConversationDetails,
} from "./actions";
import { updateOnlineStatusActionType } from "../authors/actionTypes";
import { messageStatuses } from "@const/chat";
import {
  sendDonation,
  sendDonationFailure,
  sendDonationSuccess,
} from "../payments/actions";

const initialState = {
  id: undefined, // currentActiveConversation
  isConversationDetailsLoading: false,
  isConversationsLoading: true,
  isMessagesLoading: false,
  isMessageSending: false,
  creatingConversation: false,
  lastCreatedConversation: undefined,
  isSent: false,
  allConversations: [],
  messagesByChat: [],
  messagesById: {},
  conversationsById: {},
  editMessageId: undefined,
  totalCount: undefined,
  nextOffset: undefined,
  totalMessages: undefined,
  unreadConversationCount: 0,
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(getConversations.type, (state) => ({
      ...state,
      isConversationsLoading: true,
    }))
    .addCase(getConversationsSuccess.type, (state, { payload }) => {
      const {
        allConversations = [],
        messagesById = {},
        conversationsById = {},
        totalCount,
        nextOffset,
      } = payload;

      return {
        ...state,
        messagesById: { ...state.messagesById, ...messagesById },
        allConversations,
        conversationsById: { ...state.conversationsById, ...conversationsById },
        isConversationsLoading: false,
        totalCount,
        nextOffset,
      };
    })
    .addCase(getConversationsFailure.type, (state) => ({
      ...state,
      isConversationsLoading: false,
    }))
    .addCase(getMoreConversations.type, (state) => ({
      ...state,
      isConversationsLoading: true,
    }))
    .addCase(getMoreConversationsSuccess.type, (state, { payload }) => {
      const {
        allConversations = [],
        messagesById = {},
        conversationsById = {},
        totalCount,
        nextOffset,
      } = payload;

      return {
        ...state,
        allConversations: [...state.allConversations, ...allConversations],
        conversationsById: { ...state.conversationsById, ...conversationsById },
        messagesById: { ...state.messagesById, ...messagesById },
        isConversationsLoading: false,
        totalCount,
        nextOffset,
      };
    })
    .addCase(getMoreConversationsFailure.type, (state) => ({
      ...state,
      isConversationsLoading: false,
    }))
    .addCase(getMessages.type, (state) => ({
      ...state,
      isMessagesLoading: true,
    }))
    .addCase(getMessagesSuccess.type, (state, { payload }) => ({
      ...state,
      ...payload,
      messagesByChat: {
        ...state.messagesByChat,
        [payload.id]: payload.allMessages,
      },
      isMessagesLoading: false,
      messagesById: {
        ...state.messagesById,
        ...payload.messagesById,
      },
    }))
    .addCase(getMessagesFailure.type, (state) => ({
      ...state,
      isMessagesLoading: false,
    }))
    .addCase(getMoreMessages.type, (state) => ({
      ...state,
      isMessagesFetching: true,
    }))
    .addCase(getMoreMessagesSuccess.type, (state, { payload }) => ({
      ...state,
      isMessagesFetching: false,
      totalMessages: payload.totalMessages,
      messagesById: {
        ...state.messagesById,
        ...payload.messagesById,
      },
      messagesByChat: {
        ...state.messagesByChat,
        [payload.id]: [
          ...payload.allMessages,
          ...state.messagesByChat?.[payload.id],
        ],
      },
    }))
    .addCase(getMoreMessagesFailure.type, (state) => ({
      ...state,
      isMessagesFetching: false,
    }))
    .addCase(hideConversation.type, (state, { payload }) => {
      if (state.id == payload.id) {
        state.id = undefined;
      }

      state.allConversations = state.allConversations.filter(
        (c) => c != payload.id,
      );
    })
    .addCase(conversationUpdateSuccess.type, (state, { payload }) => {
      const { conversationId, conversations } = payload;

      return {
        ...state,
        allConversations: [
          conversationId,
          ...state.allConversations.filter((c) => c != conversationId),
        ],
        conversationsById: {
          ...state.conversationsById,
          ...conversations,
        },
      };
    })
    .addCase(updateConversation.type, (state, { payload }) => {
      const { result, conversation } = payload;

      state.conversationsById[result] = conversation;
    })
    .addCase(addMessage.type, (state, { payload }) => {
      const { allMessages, messagesById, conversationId } = payload;

      return {
        ...state,
        messagesById: {
          ...state?.messagesById,
          ...messagesById,
        },
        messagesByChat: {
          ...state?.messagesByChat,
          [conversationId]: [
            ...(state?.messagesByChat?.[conversationId] || []),
            ...allMessages,
          ],
        },
      };
    })
    .addCase(addConversation.type, (state, { payload }) => {
      const { conversationId } = payload;

      state.allConversations = [
        conversationId,
        ...state.allConversations.filter((id) => id != conversationId),
      ];
    })
    .addCase(newConversationReceivedSuccess.type, (state, { payload }) => {
      state.conversationsById = {
        ...state.conversationsById,
        [payload.id]: payload,
      };

      state.allConversations = [
        ...new Set([payload.id, ...state.allConversations]),
      ];
    })
    .addCase(messageReceivedSuccess.type, (state, { payload }) => {
      const { conversationId, uuid, createdAt } = payload;
      state.messagesById[uuid] = payload;
      if (conversationId && state.conversationsById[conversationId]) {
        state.conversationsById[conversationId].lastMessage = uuid;
        state.conversationsById[conversationId].lastMessageAt =
          Date.parse(createdAt);
      }
    })
    .addCase(sendDonation.type, (state) => {
      state.isMessageSending = true;
    })
    .addCase(sendDonationSuccess.type, (state) => {
      state.isMessageSending = false;
    })
    .addCase(sendDonationFailure.type, (state) => {
      state.isMessageSending = false;
    })
    .addCase(messageReceivedUnreadCountSuccess.type, (state, { payload }) => {
      state.unreadConversationCount = payload;
    })
    .addCase(deleteMessageSuccess.type, (state, { payload }) => {
      const { id: deletedMessageId, conversationId, uuid } = payload;
      return {
        ...state,
        messagesByChat: {
          ...state.messagesByChat,
          [conversationId]: state.messagesByChat?.[conversationId].filter(
            (id) => id !== deletedMessageId && id !== uuid,
          ),
        },
        totalCount: state.totalCount - 1,
      };
    })
    .addCase(updateOnlineStatusActionType, (state, { payload: { data } }) => {
      const { userId, isOnline } = data;
      const { conversationsById } = state;

      const conversation = Object.values({ ...conversationsById }).find(
        ({ conversationOpponent }) => conversationOpponent == userId,
      );

      if (conversation) {
        conversation.isOpponentOnline = isOnline;
      }
    })
    .addCase(removeMessage.type, (state, { payload }) => {
      const { deletedMessageId, conversationId } = payload;

      return {
        ...state,
        messagesByChat: {
          ...state.messagesByChat,
          [conversationId]: state.messagesByChat?.[conversationId].filter(
            (id) => id != deletedMessageId,
          ),
        },
        totalCount: state.totalCount - 1,
      };
    })
    .addCase(sendMessage.type, (state, { payload }) => {
      const { id: conversationId, uuid, message, ...rest } = payload;

      const createdAt = new Date().toISOString();

      state.isMessageSending = true;
      state.messagesById[uuid] = {
        message,
        uuid,
        createdAt,
        conversationId,
        ...rest,
      };
      if (state.messagesByChat[conversationId]) {
        state.messagesByChat[conversationId].push(uuid);
        state.conversationsById[conversationId].lastMessageUuid = uuid;
        state.conversationsById[conversationId].lastMessageAt =
          Date.parse(createdAt);
      }
    })
    .addCase(sendMessageSuccess.type, (state, { payload }) => {
      const { message, conversationId, uuid } = payload;

      state.allConversations = [
        conversationId,
        ...state.allConversations.filter((id) => id != conversationId),
      ];

      if (message) {
        state.messagesById[uuid] = {
          ...message,
          uuid,
        };
      }

      if (conversationId && !state.conversationsById[conversationId]) {
        state.conversationsById[conversationId] = {};
      }

      if (
        state.messagesByChat[conversationId] &&
        !state.messagesByChat[conversationId].includes(uuid)
      ) {
        state.messagesByChat[conversationId].push(uuid);
      }

      if (conversationId) {
        state.conversationsById[conversationId].lastMessage = message?.id;
        state.conversationsById[conversationId].lastMessageUuid = uuid;
        state.conversationsById[conversationId].lastMessageAt = Date.parse(
          message?.createdAt,
        );
      }

      state.isMessageSending = false;
      state.isSent = true;
    })
    .addCase(sendMessageFailure.type, (state, { payload }) => {
      state.isMessageSending = false;
      state.messagesById[payload.uuid].status = messageStatuses.error;
    })
    .addCase(setIsSentStatus.type, (state, { payload }) => {
      state.isSent = payload;
    })
    .addCase(createConversation.type, (state) => {
      state.creatingConversation = true;
      state.lastCreatedConversation = initialState.lastCreatedConversation;
    })
    .addCase(createConversationSuccess.type, (state, { payload }) => {
      const { result, conversations } = payload;

      state.creatingConversation = false;
      state.lastCreatedConversation = result;
      state.conversationsById[result] = conversations[result];
    })
    .addCase(createConversationFailure.type, (state) => {
      state.creatingConversation = false;
      state.lastCreatedConversation = initialState.lastCreatedConversation;
    })
    .addCase(createConversationReset.type, (state) => {
      state.lastCreatedConversation = initialState.lastCreatedConversation;
    })
    .addCase(getConversationDetails.type, (state) => {
      state.isConversationDetailsLoading = true;
    })
    .addCase(getConversationDetailsSuccess.type, (state, { payload }) => {
      state.isConversationDetailsLoading = false;
      state.conversationsById[payload.id] = payload;
    })
    .addCase(getConversationDetailsFailure.type, (state) => {
      state.isConversationDetailsLoading = false;
    })
    .addCase(addConversationDetails.type, (state, { payload }) => {
      state.conversationsById = { ...payload, ...state.conversationsById };
    })
    .addCase(markAsReadSuccess.type, (state, { payload }) => {
      const { conversationId } = payload;
      const conversation = state.conversationsById[parseInt(conversationId)];

      if (conversation) {
        conversation.unreadMessagesCount = 0;
      }
    })
    .addCase(removeMessageFromConversation.type, (state, { payload }) => {
      const { conversationId, messageId } = payload;
      const messagesInChat = state.messagesByChat[conversationId];

      state.messagesByChat[conversationId] =
        messagesInChat?.filter((id) => id !== messageId) || [];
    })
    .addCase(leaveChat.type, (state) => {
      state.id = undefined;
      state.totalCount = undefined;
      state.totalMessages = undefined;
      state.editMessageId = undefined;
    })
    .addCase(markMessagesInConversationRead.type, (state, { payload }) => {
      state?.messagesByChat?.[Number(payload)]?.forEach((messageId) => {
        state.messagesById[messageId].status = messageStatuses.read;
      });
    })
    .addCase(getUnreadCountSuccess.type, (state, { payload }) => {
      const { count } = payload;

      state.unreadConversationCount = count;
    })
    .addCase(setEditMessageId.type, (state, { payload }) => {
      state.editMessageId = payload;
    })
    .addCase(resetEditMessageId.type, (state) => {
      state.editMessageId = undefined;
    })
    .addCase(editMessage.type, (state, { payload }) => {
      const { id, uuid } = payload;

      state.messagesById[uuid || id].isUpdating = true;
    })
    .addCase(editMessageSuccess.type, (state, { payload }) => {
      state.messagesById[payload.uuid || payload.id] = payload;
      state.isSent = true;
    })
    .addCase(editMessageFailure.type, (state, { payload }) => {
      state.messagesById[payload].isUpdating = false;
    })
    .addCase(messageEditedSuccess.type, (state, { payload }) => {
      const { id } = payload;

      state.messagesById[id] = payload;
    });
});
